import React, { useState, useEffect } from 'react';
import { api } from '../../../../services/apiServices';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Label } from '../../../../components/ui/label';
import { Badge } from '../../../../components/ui/badge';
import { useAdminStore } from '../../../../store/adminStore';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Loader2, Trash2, Tag, Plus, MapPin, Globe, Search, Eye, X } from 'lucide-react';

import LocationSearch from '../../../../components/Admin/SEO/LocationSearch';
import LocationTagSelector from '../../../../components/Admin/SEO/LocationTagSelector';

const LocationTagManager = () => {
  const { locale, countries, setLocale, setCountries } = useAdminStore((state) => state);

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // --- Definitions Tab State ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', description: '' });

  // --- View Locations Modal State (NEW) ---
  const [viewLocsModalOpen, setViewLocsModalOpen] = useState(false);
  const [viewingTag, setViewingTag] = useState(null); // { id, name }
  const [taggedLocations, setTaggedLocations] = useState([]); // List of locations
  const [loadingLocs, setLoadingLocs] = useState(false);

  // --- Assignments Tab State ---
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationTags, setLocationTags] = useState([]); 
  const [isSaving, setIsSaving] = useState(false);

  // --- Init ---
  useEffect(() => { 
    fetchTags(); 
    if (countries.length === 0) {
        api.get('/admin/common/active-countries')
          .then(res => {
             setCountries(res.data || []);
             if (!locale && res.data && res.data.length > 0) setLocale(res.data[0].country_code);
          })
          .catch(err => console.error("Failed to fetch countries:", err));
      }
  }, [countries.length, setCountries, locale, setLocale]);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/seo/location-tags');
      setTags(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers ---

  const handleCreate = async () => {
    if (!newTag.name) return;
    try {
      await api.post('/admin/seo/location-tags', newTag);
      setIsModalOpen(false);
      setNewTag({ name: '', description: '' });
      fetchTags();
    } catch (err) {
      alert(err.response?.data?.detail || "Error creating tag");
    }
  };

  const handleDelete = async (id) => {
    if(!window.confirm("Delete this tag? It will be removed from all locations.")) return;
    try {
      await api.delete(`/admin/seo/location-tags/${id}`);
      fetchTags();
    } catch (err) {
      alert("Error deleting");
    }
  };

  // --- NEW: Handle View Locations ---
  const handleViewLocations = async (tag) => {
      setViewingTag(tag);
      setViewLocsModalOpen(true);
      setLoadingLocs(true);
      setTaggedLocations([]); // Clear previous
      
      try {
          const res = await api.get(`/admin/seo/location-tags/${tag.id}/locations`);
          setTaggedLocations(res.data);
      } catch (error) {
          console.error("Failed to fetch locations", error);
      } finally {
          setLoadingLocs(false);
      }
  };

  // --- Assignment Handlers ---
  const handleLocationSelect = async (loc) => {
      setSelectedLocation(loc);
      if(!loc) return;
      try {
          const res = await api.get(`/admin/seo/location-tags/location/${loc.id}`);
          setLocationTags(res.data);
      } catch (error) {
          setLocationTags([]);
      }
  };

  const handleSaveLocationTags = async () => {
      if(!selectedLocation) return;
      setIsSaving(true);
      try {
          await api.post('/admin/seo/location-tags/assign', {
              location_id: selectedLocation.id,
              tag_ids: locationTags
          });
          fetchTags(); 
          alert(`Tags saved for ${selectedLocation.city}!`);
      } catch (error) {
          alert("Failed to save tags.");
      } finally {
          setIsSaving(false);
      }
  };

  return (
    <div className="p-6 space-y-4 bg-gray-50/50 h-full flex flex-col">
      
      {/* HEADER */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-blue-600" />
            <div>
                <h1 className="text-xl font-bold text-gray-800">Location Tags</h1>
                <p className="text-xs text-gray-500">Manage tags like "Coastal" or "Metro" and apply them to cities.</p>
            </div>
        </div>

        <div className="flex items-center gap-3 bg-blue-50 px-4 py-2 rounded-md border border-blue-100">
            <span className="text-xs font-semibold text-blue-800 uppercase tracking-wide">Context:</span>
            {countries.length > 0 ? (
                <div className="flex items-center gap-2 bg-white px-2 py-1 rounded border">
                    <Globe className="w-4 h-4 text-gray-500" />
                    <Select value={locale || ''} onValueChange={(val) => {
                        setLocale(val);
                        setSelectedLocation(null);
                    }}>
                        <SelectTrigger className="w-[150px] h-8 border-none bg-transparent focus:ring-0 p-0 text-sm font-medium">
                            <SelectValue placeholder="Select Country" />
                        </SelectTrigger>
                        <SelectContent>
                            {countries.map((c) => (
                                <SelectItem key={c.country_code} value={c.country_code}>
                                    {c.name} ({c.country_code})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            ) : (
                <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>
            )}
        </div>
      </div>

      <Tabs defaultValue="assignments" className="flex-1 flex flex-col">
          <TabsList className="w-fit bg-white border p-1 rounded-md">
              <TabsTrigger value="assignments" className="gap-2"><MapPin className="w-4 h-4"/> Tag Locations</TabsTrigger>
              <TabsTrigger value="definitions" className="gap-2"><Tag className="w-4 h-4"/> Manage Tags</TabsTrigger>
          </TabsList>

          {/* TAB 1: ASSIGN TAGS */}
          <TabsContent value="assignments" className="flex-1 mt-2">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6 h-full">
                 <div className="bg-white p-6 rounded-lg border shadow-sm space-y-6 h-fit min-h-[400px]">
                     <div className="space-y-2">
                        <Label className="text-base font-semibold flex items-center gap-2">
                            <Search className="w-4 h-4" /> Find City to Tag ({locale})
                        </Label>
                        <div className="border p-1 rounded-md bg-gray-50">
                            <LocationSearch 
                                countryCode={locale} 
                                onSelect={handleLocationSelect}
                                placeholder={`Type city name in ${locale}...`}
                            />
                        </div>
                     </div>
                     {selectedLocation ? (
                         <div className="animate-in fade-in slide-in-from-top-2 border-t pt-4 mt-4">
                             <div className="flex justify-between items-center mb-4">
                                <Label className="text-base font-semibold text-blue-900">Edit Tags for {selectedLocation.city}</Label>
                                <Badge variant="outline">{selectedLocation.state_code}</Badge>
                             </div>
                             <div className="p-4 border rounded-md bg-white shadow-sm mb-4">
                                <LocationTagSelector selectedIds={locationTags} onChange={setLocationTags} />
                             </div>
                             <Button onClick={handleSaveLocationTags} disabled={isSaving} className="w-full">
                                {isSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Save Tags
                             </Button>
                         </div>
                     ) : (
                         <div className="flex flex-col items-center justify-center py-12 text-gray-400 border-2 border-dashed rounded-lg bg-gray-50/50">
                             <MapPin className="w-12 h-12 mb-2 opacity-20" />
                             <p className="text-sm">Search for a location above to begin.</p>
                         </div>
                     )}
                 </div>
                 
                 <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-lg border border-blue-100 h-fit">
                     <h3 className="font-bold text-blue-900 mb-2">Tagging Guide</h3>
                     <p className="text-sm text-blue-700 mb-4">Tags allow grouping for automatic link building.</p>
                 </div>
             </div>
          </TabsContent>

          {/* TAB 2: MANAGE DEFINITIONS */}
          <TabsContent value="definitions" className="flex-1 bg-white border rounded-md mt-2 p-0 overflow-auto shadow-sm relative">
              <div className="p-4 border-b flex justify-between items-center bg-gray-50/50">
                  <p className="text-sm text-gray-500">Create global tags here.</p>
                  <Button onClick={() => setIsModalOpen(true)} size="sm" className="gap-2"><Plus className="w-4 h-4"/> Create New Tag</Button>
              </div>
              
              <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Tag Name</TableHead>
                        <TableHead>Tagged Locations</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ? (
                        <TableRow><TableCell colSpan={4} className="text-center py-20"><Loader2 className="animate-spin mx-auto"/></TableCell></TableRow>
                    ) : tags.map(tag => (
                        <TableRow key={tag.id}>
                            <TableCell className="font-medium">
                                <div className="flex flex-col">
                                    <span>{tag.name}</span>
                                    <span className="text-[10px] text-gray-400 font-mono">{tag.slug}</span>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Badge 
                                    variant="secondary" 
                                    className="cursor-pointer hover:bg-blue-100 transition-colors flex w-fit items-center gap-1"
                                    onClick={() => handleViewLocations(tag)}
                                >
                                    {tag.location_count || 0} Locations
                                    <Eye className="w-3 h-3 text-gray-500 ml-1"/>
                                </Badge>
                            </TableCell>
                            <TableCell className="text-gray-500 text-sm">{tag.description || '-'}</TableCell>
                            <TableCell className="text-right">
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(tag.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                    <Trash2 className="w-4 h-4"/>
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
          </TabsContent>
      </Tabs>

      {/* MODAL 1: CREATE TAG */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
            <DialogHeader><DialogTitle>Create New Tag Definition</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
                <div>
                    <Label>Tag Name</Label>
                    <Input value={newTag.name} onChange={e => setNewTag({...newTag, name: e.target.value})} placeholder="e.g. Coastal, Metro" />
                </div>
                <div>
                    <Label>Description</Label>
                    <Input value={newTag.description} onChange={e => setNewTag({...newTag, description: e.target.value})} placeholder="Optional description" />
                </div>
            </div>
            <DialogFooter>
                <Button onClick={handleCreate}>Create Tag</Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL 2: VIEW LOCATIONS (NEW) */}
      <Dialog open={viewLocsModalOpen} onOpenChange={setViewLocsModalOpen}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-blue-600"/> 
                    Locations tagged: "{viewingTag?.name}"
                </DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-auto py-2 pr-2">
                {loadingLocs ? (
                    <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400"/></div>
                ) : taggedLocations.length === 0 ? (
                    <div className="text-center py-10 text-gray-400 italic">
                        No locations have been assigned this tag yet.
                    </div>
                ) : (
                    <ul className="space-y-1">
                        {taggedLocations.map(loc => (
                            <li key={loc.id} className="flex justify-between items-center p-2 rounded hover:bg-gray-50 border border-transparent hover:border-gray-100">
                                <span className="font-medium text-sm text-gray-800">{loc.city}</span>
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-[10px]">{loc.state_code}</Badge>
                                    <span className="text-xs text-gray-400 font-mono">{loc.country_code}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            
            <DialogFooter>
                <Button variant="secondary" onClick={() => setViewLocsModalOpen(false)}>Close</Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default LocationTagManager;